import { useCallback, useState } from "react";
import { parse } from "papaparse";

export type ReadResult = {
  data?: string[];
  lineNumber: number;
  errors: string[];
  complete?: boolean;
};

export type ParserStream = {
  [Symbol.asyncIterator]: () => {
    next: () => Promise<{
      done: boolean;
      value?: ReadResult;
    }>;
    return: () => Promise<{
      done: true;
      value: undefined;
    }>;
  };
};

type ParserFunc = (file: File) => ParserStream;

const SINGLE_LINE_MEM_SIZE = 40; // max line memory size

const csvParser = (
  file: File,
  setProgress: (progress: number) => unknown,
  setParsing: (isParsing: boolean) => unknown,
) => {
  const resolveQueue: ((data: ReadResult) => void)[] = [];
  let isComplete = false;
  const estimatedLinesCount = Math.floor(file.size / SINGLE_LINE_MEM_SIZE);
  let linesCovered = 0;
  let prevProgress = 0;
  setParsing(true);
  let continueParsing: () => void;
  let stopParsing: () => void;
  parse<string[], File>(file, {
    skipEmptyLines: "greedy",
    dynamicTyping: true,
    transform: (value) => {
      return value;
    },
    step: (row, parser) => {
      if (!continueParsing) {
        continueParsing = parser.resume;
      }

      if (!stopParsing) {
        stopParsing = parser.abort;
      }

      const resolve = resolveQueue.shift();
      const hasPendingTasks = resolveQueue.length > 0;
      if (!hasPendingTasks) {
        // Therefore, we delegate file consumption control solely to the consumer
        parser.pause();
      }
      if (resolve) {
        resolve({
          data: row.data,
          errors: row.errors.map((error) => error.message),
          lineNumber: linesCovered,
        });
      }
      linesCovered++;
      const currentProgress = Math.floor(
        (linesCovered / estimatedLinesCount) * 100,
      );
      const adjustedProgress = Math.min(currentProgress, 90);
      //refresh progress with at least 10% step
      if (adjustedProgress - prevProgress > 10) {
        setProgress(adjustedProgress);
      }
      prevProgress = adjustedProgress;
    },
    error: (error) => {
      const resolve = resolveQueue.shift();
      if (resolve) {
        resolve({ lineNumber: linesCovered, errors: [error.message] });
      }
    },
    complete: () => {
      resolveQueue.forEach((resolve) =>
        resolve({ lineNumber: linesCovered, errors: [], complete: true }),
      );
      isComplete = true;
      setProgress(100);
      setParsing(false);
    },
  });

  return {
    [Symbol.asyncIterator]: () => {
      return {
        next: async () => {
          if (isComplete) {
            return { done: true, value: undefined };
          }
          const currentPromise = new Promise<ReadResult>((resolve) => {
            resolveQueue.push(resolve);
          });
          if (continueParsing) {
            continueParsing();
          }
          const lineReadResult = await currentPromise;
          if (lineReadResult.complete) {
            isComplete = true;
            return { done: true, value: undefined };
          }
          return { done: false, value: lineReadResult };
        },

        return: async () => {
          if (stopParsing) {
            stopParsing();
          }
          isComplete = true;
          setParsing(false);
          setProgress(0);
          //don't leave promises hanging
          resolveQueue.forEach((resolve) =>
            resolve({ lineNumber: linesCovered, errors: [], complete: true }),
          );
          return { done: true as const, value: undefined };
        },
      };
    },
  };
};

export const useCsvParser = () => {
  const [isParsing, setIsParsing] = useState(false);
  const [progress, setProgress] = useState(0);
  const parseIteratorBuilder: ParserFunc = useCallback(
    (file: File) => csvParser(file, setProgress, setIsParsing),
    [setIsParsing, setProgress],
  );

  const reset = useCallback(() => {
    setIsParsing(false);
    setProgress(0);
  }, [setIsParsing, setProgress]);

  return {
    isParsing,
    progress,
    parseIteratorBuilder,
    reset,
  };
};
